import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, WifiSlash as WifiOff, Money as Banknote, UsersThree as UsersRound, Package as Boxes, ChartPie as PieChart, EnvelopeSimple as MailCheck, GitBranch } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO, { buildFaqJsonLd } from '../../components/SEO';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { node.classList.add('revealed'); obs.unobserve(node); } },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);
  return ref;
};

const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const GHANA_REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Northern',
  'Upper East', 'Upper West', 'Central', 'Volta', 'Bono',
  'Bono East', 'Ahafo', 'Western North', 'Oti', 'North East', 'Savannah',
];

const GHANA_FAQS = [
  {
    question: 'Does Smart POS Africa support MTN MoMo, Vodafone Cash, and AirtelTigo Money in Ghana?',
    answer: 'Yes. Smart POS Africa supports split payments across cash, MTN Mobile Money, Vodafone Cash, AirtelTigo Money, and card – all in a single transaction. Your cashier selects how the customer is paying and the system records each payment method separately.',
  },
  {
    question: 'Does Smart POS Africa work without internet in Ghana?',
    answer: 'Yes. Smart POS Africa is built to work fully offline. Every sale is saved locally on the device and syncs automatically when your connection returns. No sale is ever lost to a network drop or power cut.',
  },
  {
    question: 'How much does Smart POS Africa cost in Ghana?',
    answer: 'Smart POS Africa starts at GHS 300 per month, or GHS 3,000 per year (saving 17%). Every plan includes all features, unlimited staff accounts, and multi-branch support. There are no hidden fees and no per-user charges. A one-month free trial is available with no credit card required.',
  },
  {
    question: 'Can I manage multiple branches across Ghana from one account?',
    answer: 'Yes. You can add as many branches as you need and view sales, inventory, and staff performance across all locations from a single dashboard. Additional branches start from GHS 200 per month.',
  },
  {
    question: 'What types of businesses in Ghana use Smart POS Africa?',
    answer: 'Smart POS Africa is used by retailers, restaurants, chop bars, salons, pharmacies, mini marts, supermarkets, and service businesses across Ghana. It works for any business that needs to sell, track inventory, and manage staff.',
  },
  {
    question: 'Do I need special hardware to use Smart POS Africa in Ghana?',
    answer: 'No. Smart POS Africa runs in any browser on Android phones, tablets, or computers – the devices your staff already have. If you have a Bluetooth or USB receipt printer, it will connect automatically. No new hardware is required to get started.',
  },
];

const ghanaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Smart POS Africa',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Android, iOS',
  description: 'Best POS software in Ghana. Works offline, supports MTN MoMo, Vodafone Cash, and AirtelTigo Money. Transparent GHS pricing from GHS 300/month.',
  url: 'https://smartposafrica.com/ghana',
  areaServed: { '@type': 'Country', name: 'Ghana' },
  offers: {
    '@type': 'Offer',
    price: '300',
    priceCurrency: 'GHS',
    description: 'From GHS 300/month. One month free trial, no credit card required.',
  },
};

const features = [
  {
    icon: WifiOff,
    accent: 'bg-emerald-500/[0.08]',
    iconColor: 'text-emerald-600',
    title: 'Full offline mode',
    description: 'Sell, track inventory, and manage staff even when your internet is down. Every sale is saved locally and syncs automatically when you reconnect.',
  },
  {
    icon: Banknote,
    accent: 'bg-yellow-500/[0.08]',
    iconColor: 'text-yellow-600',
    title: 'MTN MoMo, Vodafone Cash & AirtelTigo',
    description: 'Accept all major Ghanaian mobile money networks. Let customers split a single payment across cash, mobile money, and card.',
  },
  {
    icon: Boxes,
    accent: 'bg-amber-500/[0.08]',
    iconColor: 'text-amber-600',
    title: 'Inventory management',
    description: 'Stock levels update automatically with every sale. Know when to restock before you run out – across every branch and location.',
  },
  {
    icon: UsersRound,
    accent: 'bg-violet-500/[0.08]',
    iconColor: 'text-violet-600',
    title: 'Staff management',
    description: 'Every transaction is linked to a named staff member. Set role-based permissions and track who sold what, when, and how much.',
  },
  {
    icon: PieChart,
    accent: 'bg-blue-500/[0.08]',
    iconColor: 'text-blue-600',
    title: 'Real-time analytics',
    description: 'Live sales data, profit tracking, and expense visibility. Make decisions based on numbers, not guesswork.',
  },
  {
    icon: GitBranch,
    accent: 'bg-rose-500/[0.08]',
    iconColor: 'text-rose-600',
    title: 'Multi-branch support',
    description: 'Run one location or ten. See consolidated sales, inventory, and staff performance across all branches from a single dashboard.',
  },
];

const GhanaPage = () => {
  const faqJsonLd = buildFaqJsonLd(GHANA_FAQS.map(({ question, answer }) => ({ question, answer })));

  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title="Best POS Software in Ghana"
        description="Smart POS Africa is the best POS software for Ghanaian businesses. Works offline, supports MTN MoMo, Vodafone Cash and AirtelTigo. Priced in GHS from GHS 300/month. Free trial, no credit card."
        path="/ghana"
        keywords="best pos software ghana, pos software ghana, pos system ghana, point of sale ghana, retail pos ghana, restaurant pos ghana, pos ghana 2026, mobile money pos ghana, mtn momo pos ghana, vodafone cash pos, pos software accra"
        jsonLd={[ghanaJsonLd, faqJsonLd]}
        geoRegion="GH"
        geoPlacename="Ghana"
      />
      <LandingNav />

      {/* ━━━ HERO ━━━ */}
      <section className="pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <R>
              <div className="inline-flex items-center gap-2 bg-emerald-500/[0.08] border border-emerald-500/[0.12] rounded-full px-4 py-1.5 mb-5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span className="text-emerald-800 text-xs font-bold tracking-wide uppercase">Ghana</span>
              </div>
            </R>
            <R delay={80}>
              <h1 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-extrabold text-primaryB-900 leading-[1.35] tracking-tight mb-6">
                Best POS Software in Ghana
              </h1>
            </R>
            <R delay={150}>
              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed max-w-xl mb-8">
                Built for how Ghanaian businesses actually work – offline when the network drops, mobile money at checkout, GHS pricing, and full visibility across every branch.
              </p>
            </R>
            <R delay={200}>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-2xl transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Start free trial <ArrowRight weight="bold" className="w-4 h-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 bg-primaryB-100 hover:bg-primaryB-200 text-primaryB-800 font-bold px-6 py-3 rounded-2xl transition-all duration-200"
                >
                  See GHS pricing
                </Link>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ STATS BAR ━━━ */}
      <section className="border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-primaryB-200/60">
            {[
              { value: 'GHS 300', label: 'per month' },
              { value: '1 month', label: 'Free trial' },
              { value: '100%', label: 'Offline capable' },
              { value: '5 min', label: 'Setup time' },
            ].map((stat) => (
              <div key={stat.label} className="py-6 sm:py-10 text-center px-2">
                <div className="text-xl sm:text-3xl font-extrabold text-primaryB-900 mb-0.5 sm:mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-primaryB-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ THE GHANA REALITY ━━━ */}
      <section className="py-16 sm:py-24 bg-primaryB-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-400 text-sm font-bold tracking-wide uppercase mb-4">The reality</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-5 max-w-2xl">
              Running a business in Ghana isn't like running one in London or New York.
            </h2>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl mb-12">
              Power cuts happen. Network drops happen. Most customers pay with mobile money, not card. Most POS software was built for markets where none of this is true. Smart POS Africa was built for yours.
            </p>
          </R>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                problem: 'Network and power outages',
                solution: 'Works 100% offline. Every sale saved locally and syncs automatically when the connection returns. Never lose a transaction.',
              },
              {
                problem: 'Mobile money is how customers pay',
                solution: 'MTN MoMo, Vodafone Cash, and AirtelTigo built in. Accept split payments across cash, mobile money, and card in one transaction.',
              },
              {
                problem: 'Managing staff across branches',
                solution: 'One dashboard for all locations. See sales, stock, and staff performance across every branch in real time.',
              },
            ].map((item, i) => (
              <R key={item.problem} delay={i * 100}>
                <div className="bg-white/[0.05] border border-white/[0.08] rounded-3xl p-6">
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wide mb-2">The problem</p>
                  <p className="text-white font-bold text-base mb-5">{item.problem}</p>
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wide mb-2">The solution</p>
                  <p className="text-white/70 text-sm leading-relaxed">{item.solution}</p>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ FEATURES ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">What's included</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-3">
              Everything you need to run your Ghanaian business
            </h2>
            <p className="text-primaryB-500 text-base sm:text-lg mb-12 max-w-xl">
              One plan. Every feature. No add-ons or upgrades.
            </p>
          </R>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <R key={feature.title} delay={i * 60}>
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border border-primaryB-200/60 hover:shadow-card hover:border-primaryB-300/80 transition-all duration-200 group h-full">
                    <div className={`w-10 h-10 rounded-xl ${feature.accent} flex items-center justify-center mb-3.5 transition-transform duration-200 group-hover:scale-110`}>
                      <Icon weight="bold" className={`w-5 h-5 ${feature.iconColor}`} />
                    </div>
                    <h3 className="text-primaryB-900 font-bold text-sm mb-1.5">{feature.title}</h3>
                    <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </R>
              );
            })}
          </div>
          <R delay={100}>
            <div className="mt-8 text-center">
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200"
              >
                See all features <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ BUSINESS TYPES ━━━ */}
      <section className="py-14 sm:py-20 border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-3">
              Built for every type of Ghanaian business
            </h2>
            <p className="text-primaryB-500 text-base mb-8 max-w-xl">
              Whether you run a single shop or multiple branches across Greater Accra and beyond, Smart POS Africa adapts to how you operate.
            </p>
          </R>
          <R delay={80}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
              <span className="text-primaryB-300 text-sm font-semibold whitespace-nowrap shrink-0">Works for</span>
              <div className="flex flex-wrap gap-2">
                {[
                  'Retail shops', 'Restaurants', 'Chop bars', 'Salons & barbershops',
                  'Pharmacies', 'Mini marts', 'Supermarkets', 'Boutiques',
                  'Electronics shops', 'Hardware stores', 'Wholesale distributors', 'Service businesses',
                ].map((type) => (
                  <span
                    key={type}
                    className="px-3.5 py-1.5 rounded-full bg-white border border-primaryB-200/60 text-primaryB-700 text-xs sm:text-sm font-medium"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ PRICING ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <R>
              <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-4">Transparent pricing</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-5">
                Priced in GHS.<br />No surprises.
              </h2>
              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-6">
                Unlike POS software priced in dollars, Smart POS Africa bills you in cedis. What you see is what you pay – no conversion, no exchange rate fluctuation.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'All features included – nothing locked behind upgrades',
                  'Unlimited staff accounts at no extra cost',
                  'Multi-branch support from GHS 200/month per extra branch',
                  'One month free trial – no credit card required',
                  'Cancel anytime',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-2" />
                    <span className="text-primaryB-700 text-sm">{point}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200"
              >
                View full pricing <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </R>
            <R delay={100}>
              <div className="bg-primaryB-900 rounded-3xl p-8 sm:p-10">
                <p className="text-white/50 text-sm mb-2">Starting from</p>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-4xl sm:text-5xl font-extrabold text-white">GHS 300</span>
                  <span className="text-white/50 pb-1">/month</span>
                </div>
                <p className="text-emerald-400 text-sm mb-8">or GHS 3,000/year – save 17%</p>
                <div className="space-y-2.5 mb-8">
                  {[
                    'Full POS with barcode scanning',
                    'Inventory management',
                    'Staff management & activity logs',
                    'Expense tracking',
                    'Real-time analytics & reports',
                    'Daily email summaries',
                    'Receipt printing',
                    'MTN MoMo, Vodafone Cash & AirtelTigo',
                    'Works fully offline',
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <span className="w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0" />
                      <span className="text-white/70 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/signup"
                  className="block text-center bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3.5 rounded-2xl transition-all duration-200"
                >
                  Start free trial
                </Link>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ REGIONS ━━━ */}
      <section className="py-14 sm:py-20 border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">Coverage</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-3">
              Serving businesses across all 16 regions of Ghana
            </h2>
            <p className="text-primaryB-500 text-base mb-8">
              From Greater Accra to the Northern Savannah – Smart POS Africa works wherever your business is.
            </p>
          </R>
          <R delay={80}>
            <div className="flex flex-wrap gap-2">
              {GHANA_REGIONS.map((region) => (
                <span
                  key={region}
                  className="bg-white border border-primaryB-200/60 text-primaryB-600 text-sm px-3 py-1.5 rounded-full"
                >
                  {region}
                </span>
              ))}
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ FAQ ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-10">
              Questions from Ghanaian business owners
            </h2>
          </R>
          <div className="divide-y divide-primaryB-200/60">
            {GHANA_FAQS.map((faq, i) => (
              <R key={faq.question} delay={i * 60}>
                <div className="py-6 sm:py-7">
                  <h3 className="text-base sm:text-lg font-bold text-primaryB-900 mb-3">{faq.question}</h3>
                  <p className="text-primaryB-500 text-sm sm:text-base leading-relaxed">{faq.answer}</p>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ FINAL CTA + FOOTER (shared dark wrapper prevents rounded-corner artifact) ━━━ */}
      <div className="bg-primaryB-900">
        <section className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <R>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-5">
                Start selling smarter in Ghana today
              </h2>
              <p className="text-white/60 text-base sm:text-lg mb-8 max-w-lg mx-auto">
                Set up in 5 minutes. No hardware needed. One month free – no credit card required.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-7 py-3.5 rounded-2xl transition-all duration-200"
                >
                  Start free trial <ArrowRight weight="bold" className="w-4 h-4" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-white/[0.08] hover:bg-white/[0.12] text-white font-bold px-7 py-3.5 rounded-2xl transition-all duration-200"
                >
                  Learn more
                </Link>
              </div>
            </R>
          </div>
        </section>

        <LandingFooter />
      </div>
    </div>
  );
};

export default GhanaPage;
